import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { ErrorToastWishList } from '../constants/wish-list-error-toast-status';
import { ProductCardData } from '../../product-card/product-card-display';
import { WishListAnalytics } from '../utils/wish-list-analytics.util';
import { WishListCommentElement } from '../interfaces/wish-list-comment.interface';
import { WishListCommentUtil } from '../utils/wish-list-comment.util';
import { addProductToWishList } from '../thunks/wish-list-add-product.thunk';
import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { getAddProductToWishListRequest } from '../utils/wish-list-product.util';
import { html, nothing, TemplateResult } from 'lit';
import { isAddProductToWishListSuccess } from '../utils/wish-list.util';
import { renderErrorToast } from '../utils/wish-list-toast.util';
import { waitForElementsToLoad } from '../../../common/utils/delay-util';
import { fetchWishListByWishListId } from '../thunks/wish-list-fetch-wish-list-by-id.thunks';

@customElement('mhecomm-wish-list-item-comment')
export class WishListItemComment extends connect(store)(BaseComponent) implements WishListCommentElement {
  /**
   * The maximum size of the comment.
   */
  @property({ type: Number })
  commentMaxSize = 300;

  /**
   * The input comment to initialize the component.
   */
  @property()
  inputComment = '';

  /**
   * The placeholder text for the comment text area.
   */
  @property()
  placeholderComment = 'Type to add a comment';

  /**
   * The product related to the comment.
   */
  @property({ type: Object })
  product?: ProductCardData;

  /**
   * The wish list ID related to the comment.
   */
  @property({ type: Number })
  wishListId = 0;

  /**
   * The value of the comment text area.
   */
  @state()
  private comment = '';

  /**
   * The size of the comment.
   */
  @state()
  private commentSize = 0;

  /**
   * A flag to hold the state of the errors.
   */
  @state()
  private errors = false;

  /**
   * A flag to indicate if the comment section is expanded.
   */
  @state()
  private isExpanded = false;

  /**
   * A flag to indicate if the comment is being saved.
   */
  @state()
  private isSaving = false;

  /**
   * A flag to indicate if the wish list add product API call was successful.
   */
  @state()
  private isAddProductToWishListSuccess?: boolean = undefined;

  /**
   * The utility class for handling comment-related functionalities.
   */
  private commentUtil?: WishListCommentUtil;

  /**
   * The collapse instance for the comment section.
   */
  private collapse: any;

  /**
   * The identifier for comment elements in the DOM.
   */
  private identifier = '';

  /**
   * A flag indicating if the comment was added or updated.
   */
  private wasCommentUpdated = false;

  /**
   * The analytics instance for the wish list.
   */
  private readonly analytics: WishListAnalytics;

  constructor() {
    super();
    this.analytics = new WishListAnalytics();
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.identifier = `wish-list-${this.wishListId}-item-${this.product?.isbn}`;
    this.commentUtil = new WishListCommentUtil(this, this.identifier);
  }

  renderComp(): TemplateResult {
    return html`
      <form action="#" method="POST">
        <div class="mb-2">
          <strong>Comments:</strong>
        </div>
        ${this.commentUtil!.renderCommentValue(this.isExpanded, this.comment)}
        <div class="mt-2">
          ${this.commentUtil!.renderCollapseToggleButton(this.isExpanded, this.comment, this.isSaving)}
          ${this.commentUtil!.renderCommentCollapse(
            this.commentSize,
            this.commentMaxSize,
            this.errors,
            this.placeholderComment,
            this.isSaving
          )}
        </div>
      </form>
      ${this.renderToast()}
    `;
  }

  /**
   * Updates the comment size from the input event.
   *
   * @param event
   */
  updateCommentFromInput(event: InputEvent): void {
    const comment = (event.target as HTMLInputElement).value ?? '';
    this.commentSize = comment.length;
  }

  /**
   * Collapses the comment section and resets the comment.
   */
  cancelComment(): void {
    this.analytics.comment(this, '', this.wishListId, this.product?.isbn ?? '', 'Cancel', true);
    this.toggleCollapse();
    this.fillComment();
  }

  /**
   * Handles the collapse toggle event.
   */
  handleCollapseToggle(event: Event): void {
    event.preventDefault();
    this.toggleCollapse();
  }

  /**
   * Updates the product with the comment on save.
   */
  saveComment(): void {
    this.errors = false;
    this.isAddProductToWishListSuccess = undefined;

    const comment = this.commentUtil!.getCommentElement(this.shadowRoot!)?.value ?? '';
    if (!this.commentUtil!.isValidComment(comment)) {
      this.errors = true;
      return;
    }

    this.analytics.comment(this, comment, this.wishListId, this.product?.isbn ?? '', 'Save Comment');
    this.isSaving = true;

    store
      .dispatch(addProductToWishList(getAddProductToWishListRequest(comment, this.product, this.wishListId)))
      .then((): void => this.handleResponse(comment))
      .catch((): void => this.handleResponse(comment))
      .finally((): void => {
        this.isSaving = false;
      });
  }

  /**
   * Handles the response when updating a product comment on a wish list.
   *
   * @param comment
   */
  private handleResponse(comment: string): void {
    const isSuccess = isAddProductToWishListSuccess();
    if (!isSuccess) {
      this.triggerToastAndRefreshWishlist(false);
      return;
    }

    this.wasCommentUpdated = !!this.comment;
    this.comment = comment;
    this.toggleCollapse();
    this.triggerToastAndRefreshWishlist(true);
  }

  /**
   * Updates the state to trigger the toast after a delay to take into account the collapse animation.
   *
   * if isSuccess is true, it will refresh the wishlist data. Then, the toast will only be displayed at the end.
   *
   * @param isSuccess
   */
  private triggerToastAndRefreshWishlist(isSuccess: boolean): void {
    setTimeout((): void => {
      if (!isSuccess) {
        this.isAddProductToWishListSuccess = false;
        return;
      }

      store.dispatch(fetchWishListByWishListId(this.wishListId)).finally((): void => {
        this.isAddProductToWishListSuccess = true;
      });
    }, 500);
  }

  /**
   * Sets or resets the comment from the input comment property.
   */
  private fillComment(): void {
    this.comment = this.inputComment;
    this.commentSize = this.inputComment.length;

    waitForElementsToLoad(this.shadowRoot!, [`#${this.identifier}-input`]).then((): void => {
      const commentElement = this.commentUtil?.getCommentElement(this.shadowRoot!);
      if (commentElement) {
        commentElement.value = this.comment;
      }
    });
  }

  /**
   * Renders the toast message for adding/updating a product comment on a wish list.
   */
  private renderToast(): TemplateResult | typeof nothing {
    if (this.isAddProductToWishListSuccess === undefined) {
      return nothing;
    }

    return this.isAddProductToWishListSuccess ? this.renderCommentUpdateSuccess() : this.renderError();
  }

  /**
   * Renders the error when adding/updating a product with a comment on a wish list.
   */
  private renderError(): TemplateResult {
    return html`
      ${renderErrorToast({
        error: ErrorToastWishList.UPDATING_WISH_LIST_COMMENT,
        displayLoadingTemplate: false,
        autoHide: false,
        hideClose: true,
      })}
    `;
  }

  /**
   * Renders the success toast when adding/updating a product with a comment on a wish list.
   */
  private renderCommentUpdateSuccess(): TemplateResult {
    return html`
      <mhecomm-toast
        .body=${html`The comment has been successfully ${this.wasCommentUpdated ? 'updated' : 'added'}.`}
        type="light"
        ?hideClose="${true}"
        ?autoHide="${false}"
        ?showOnlyBody="${true}"
        @toast-close="${() => (this.isAddProductToWishListSuccess = undefined)}"
      ></mhecomm-toast>
    `;
  }

  /**
   * Expends or collapses the comment section.
   */
  private toggleCollapse(): void {
    this.collapse?.toggle();
    this.isExpanded = !this.isExpanded;
  }

  /**
   * Initializes the collapse instance for the comment section.
   *
   * If the input comment property changes, it will fill the textarea.
   */
  updated(_changedProperties: Map<string | number | symbol, unknown>): void {
    super.updated(_changedProperties);

    this.collapse = this.bsInit(`#${this.identifier}-comment`, window.bootstrap?.Collapse, { toggle: false })?.instance;
    if (_changedProperties.has('inputComment')) {
      this.fillComment();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-wish-list-item-comment': WishListItemComment;
  }
}
